import { useMemo } from 'react';
import { Score } from '../types/score';

/**
 * Hook pour calculer les données de score de manière dynamique
 * @param overallScore - Score global
 * @param performanceMetrics - Métriques de performance
 * @param siteUrl - URL du site analysé
 * @returns Objet Score avec données calculées
 */
export function useScoreData(
  overallScore: number,
  performanceMetrics?: any,
  siteUrl?: string
): Score {
  return useMemo(() => {
    // Calcul dynamique des scores par catégorie basé sur les vraies métriques
    const calculateCategoryScores = () => {
      const baseScore = overallScore || 0;
      const hasQuiz = performanceMetrics?.quizScores?.hasQuiz || false;
      const quizData = performanceMetrics?.quizScores || { maturity: 0, acquisition: 0, innovation: 0 };

      // ===============================================
      // 1. MATURITÉ DIGITALE (Infrastructure technique)
      // ===============================================
      const digitalMaturityFactors = {
        // Performance technique (40%)
        performance: performanceMetrics?.performance || 0,
        mobilePerf: performanceMetrics?.mobile_performance_score || 0,
        desktopPerf: performanceMetrics?.desktop_performance_score || 0,

        // Bonnes pratiques (30%)
        bestPractices: performanceMetrics?.technicalScore || performanceMetrics?.bestPractices || 0,

        // Temps de chargement (20%)
        loadingTime: performanceMetrics?.loadingTime ?
          Math.max(0, 100 - (parseFloat(performanceMetrics.loadingTime) * 10)) : 0,

        // Problèmes critiques (10% - pénalité)
        criticalIssues: performanceMetrics?.criticalIssues || 0
      };

      let digitalMaturityScore = 0;

      if (hasQuiz && quizData.maturity > 0) {
        // Avec Quiz: 60% quiz + 40% technique
        const technicalScore = digitalMaturityFactors.performance > 0 ?
          Math.min(100, Math.max(0,
            (digitalMaturityFactors.performance * 0.4) +
            (digitalMaturityFactors.bestPractices * 0.3) +
            (digitalMaturityFactors.loadingTime * 0.2) +
            ((digitalMaturityFactors.mobilePerf + digitalMaturityFactors.desktopPerf) / 2 * 0.1) -
            (digitalMaturityFactors.criticalIssues * 2)
          ))
          : baseScore * 0.8;

        digitalMaturityScore = (quizData.maturity * 0.6) + (technicalScore * 0.4);
      } else {
        // Sans Quiz: 100% technique
        digitalMaturityScore = digitalMaturityFactors.performance > 0 ?
          Math.min(100, Math.max(0,
            (digitalMaturityFactors.performance * 0.4) +
            (digitalMaturityFactors.bestPractices * 0.3) +
            (digitalMaturityFactors.loadingTime * 0.2) +
            ((digitalMaturityFactors.mobilePerf + digitalMaturityFactors.desktopPerf) / 2 * 0.1) -
            (digitalMaturityFactors.criticalIssues * 2)
          ))
          : Math.max(50, baseScore * 0.8);
      }

      // ===============================================
      // 2. ACQUISITION & MARKETING (Visibilité & Trafic)
      // ===============================================
      const acquisitionFactors = {
        // SEO composite si disponible (50%)
        seoComposite: performanceMetrics?.seoComposite || 0,
        seoBasic: performanceMetrics?.seo || 0,

        // Détails SEO si disponibles (30%)
        visibilityScore: performanceMetrics?.seoDetails?.visibilityScore || 0,
        nonBrandShare: performanceMetrics?.seoDetails?.nonBrandShare || 0,
        onPageScore: performanceMetrics?.seoDetails?.onPageScore || 0,

        // Mots-clés analysés (10%)
        keywordsCount: performanceMetrics?.seoDetails?.keywordsAnalyzed || 0,
        totalSearchVolume: performanceMetrics?.seoDetails?.totalSearchVolume || 0,

        // Warnings SEO (10% - pénalité)
        seoWarnings: performanceMetrics?.warnings || 0
      };

      const hasComposite = acquisitionFactors.seoComposite > 0;
      const hasDetails = acquisitionFactors.visibilityScore > 0;

      let acquisitionScore = 0;
      if (hasComposite) {
        acquisitionScore = acquisitionFactors.seoComposite;

        // Bonus si beaucoup de mots-clés
        if (acquisitionFactors.keywordsCount > 10) {
          acquisitionScore += Math.min(5, acquisitionFactors.keywordsCount / 4);
        }

        // Pénalité warnings
        acquisitionScore -= (acquisitionFactors.seoWarnings * 0.5);

      } else if (hasDetails) {
        acquisitionScore = (
          (acquisitionFactors.visibilityScore * 0.5) +
          (acquisitionFactors.nonBrandShare * 0.3) +
          (acquisitionFactors.onPageScore * 0.2)
        );
      } else if (acquisitionFactors.seoBasic > 0) {
        acquisitionScore = acquisitionFactors.seoBasic;
      } else {
        acquisitionScore = Math.max(40, baseScore * 0.7);
      }

      acquisitionScore = Math.min(100, Math.max(0, acquisitionScore));

      // Intégrer le quiz si disponible
      if (hasQuiz && quizData.acquisition > 0) {
        // Avec Quiz: 70% quiz + 30% SEO technique
        acquisitionScore = (quizData.acquisition * 0.7) + (acquisitionScore * 0.3);
      }

      // ===============================================
      // 3. INNOVATION (Technologies & UX moderne)
      // ===============================================
      const innovationFactors = {
        // Accessibilité (40%)
        accessibility: performanceMetrics?.accessibility || performanceMetrics?.accessibilityScore || 0,

        // Technologies modernes (30%) - depuis bestPractices
        modernTech: performanceMetrics?.bestPractices || performanceMetrics?.technicalScore || 0,

        // UX Score composite (20%)
        uxScore: performanceMetrics?.uxScore || 0,

        // First Contentful Paint (10%)
        fcp: performanceMetrics?.firstContentfulPaint ?
          Math.max(0, 100 - (parseFloat(performanceMetrics.firstContentfulPaint) * 20)) : 0
      };

      let innovationScore = 0;

      if (hasQuiz && quizData.innovation > 0) {
        // Avec Quiz: 80% quiz + 20% technique (innovation = plus subjectif)
        const technicalInnovation = innovationFactors.accessibility > 0 ?
          Math.min(100, Math.max(0,
            (innovationFactors.accessibility * 0.4) +
            (innovationFactors.modernTech * 0.3) +
            (innovationFactors.uxScore * 0.2) +
            (innovationFactors.fcp * 0.1)
          ))
          : baseScore * 0.6;

        innovationScore = (quizData.innovation * 0.8) + (technicalInnovation * 0.2);
      } else {
        // Sans Quiz: 100% technique
        innovationScore = innovationFactors.accessibility > 0 ?
          Math.min(100, Math.max(0,
            (innovationFactors.accessibility * 0.4) +
            (innovationFactors.modernTech * 0.3) +
            (innovationFactors.uxScore * 0.2) +
            (innovationFactors.fcp * 0.1)
          ))
          : Math.max(30, baseScore * 0.6);
      }

      return {
        digitalMaturity: Math.round(digitalMaturityScore),
        acquisition: Math.round(acquisitionScore),
        innovation: Math.round(innovationScore)
      };
    };

    const categoryScores = calculateCategoryScores();

    return {
      overall: Math.round(overallScore || 0),
      categories: [
        { 
          name: 'Maturité Digitale', 
          score: categoryScores.digitalMaturity,
          description: 'Performance technique et infrastructure'
        },
        { 
          name: 'Acquisition & Marketing', 
          score: categoryScores.acquisition,
          description: 'SEO, visibilité et conversion'
        },
        { 
          name: 'Innovation', 
          score: categoryScores.innovation,
          description: 'Technologies modernes et accessibilité'
        }
      ],
      siteUrl,
      lastUpdated: new Date().toISOString()
    };
  }, [overallScore, performanceMetrics, siteUrl]);
}

/**
 * Hook pour obtenir des recommandations basées sur le score
 * @param score - Objet Score
 * @returns Recommandations personnalisées
 */
export function useScoreRecommendations(score: Score) {
  return useMemo(() => {
    const recommendations: Array<{
      category: string;
      priority: 'high' | 'medium' | 'low';
      title: string;
      description: string;
      impact: string;
    }> = [];

    // Recommandations basées sur la maturité digitale
    const digitalMaturity = score.categories.find(c => c.name === 'Maturité Digitale');
    if (digitalMaturity && digitalMaturity.score < 70) {
      recommendations.push({
        category: 'Technique',
        priority: 'high',
        title: 'Optimiser les performances techniques',
        description: 'Améliorer la vitesse de chargement et l\'infrastructure',
        impact: 'Amélioration de l\'expérience utilisateur et du SEO'
      });
    }

    // Recommandations basées sur l'acquisition
    const acquisition = score.categories.find(c => c.name === 'Acquisition & Marketing');
    if (acquisition && acquisition.score < 60) {
      recommendations.push({
        category: 'Marketing',
        priority: 'high',
        title: 'Renforcer la stratégie SEO',
        description: 'Optimiser le contenu et la structure pour les moteurs de recherche',
        impact: 'Augmentation du trafic organique'
      });
    }

    // Recommandations basées sur l'innovation
    const innovation = score.categories.find(c => c.name === 'Innovation');
    if (innovation && innovation.score < 50) {
      recommendations.push({
        category: 'Innovation',
        priority: 'medium',
        title: 'Moderniser les technologies',
        description: 'Adopter des technologies plus récentes et améliorer l\'accessibilité',
        impact: 'Meilleure expérience utilisateur et conformité'
      });
    }

    return recommendations;
  }, [score]);
}

/**
 * Hook pour formater les données de score pour l'affichage
 * @param score - Objet Score
 * @returns Données formatées pour l'UI
 */
export function useFormattedScoreData(score: Score) {
  return useMemo(() => {
    const getScoreColor = (scoreValue: number): string => {
      if (scoreValue >= 80) return 'text-green-600';
      if (scoreValue >= 60) return 'text-yellow-600';
      return 'text-red-600';
    };

    const getScoreGrade = (scoreValue: number): string => {
      if (scoreValue >= 90) return 'A+';
      if (scoreValue >= 80) return 'A';
      if (scoreValue >= 70) return 'B';
      if (scoreValue >= 60) return 'C';
      if (scoreValue >= 50) return 'D';
      return 'F';
    };

    return {
      overall: {
        score: score.overall,
        grade: getScoreGrade(score.overall),
        color: getScoreColor(score.overall),
        percentage: `${score.overall}%`
      },
      categories: score.categories.map(category => ({
        ...category,
        grade: getScoreGrade(category.score),
        color: getScoreColor(category.score),
        percentage: `${category.score}%`
      }))
    };
  }, [score]);
}
